/**
 * useSmartLists Hook
 * 
 * CRUD operations for saved filter configurations (Smart Lists).
 * Uses the crm_smart_lists table in Supabase.
 * 
 * Features:
 * - Load user's personal smart lists
 * - Load global (shared) smart lists
 * - Create, update, delete smart lists
 * - Track usage count for sorting by popularity
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabase, isSupabaseConfigured } from '../utils/supabase';

/**
 * Transform snake_case smart list to camelCase
 */
function transformSmartListToCamelCase(row) {
  if (!row) return null;
  
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    createdBy: row.created_by,
    isGlobal: row.is_global,
    isDefault: row.is_default,
    filters: row.filters || [],
    sortBy: row.sort_by,
    sortDirection: row.sort_direction,
    visibleColumns: row.visible_columns || [],
    usageCount: row.usage_count || 0,
    lastUsedAt: row.last_used_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Entity type for distinguishing contacts vs deals lists
    entityType: row.entity_type || 'contacts',
  };
}

/**
 * Transform camelCase to snake_case for database
 */
function transformSmartListToSnakeCase(data) {
  const row = {};
  
  if (data.name !== undefined) row.name = data.name;
  if (data.description !== undefined) row.description = data.description;
  if (data.createdBy !== undefined) row.created_by = data.createdBy;
  if (data.isGlobal !== undefined) row.is_global = data.isGlobal;
  if (data.isDefault !== undefined) row.is_default = data.isDefault;
  if (data.filters !== undefined) row.filters = data.filters;
  if (data.sortBy !== undefined) row.sort_by = data.sortBy;
  if (data.sortDirection !== undefined) row.sort_direction = data.sortDirection;
  if (data.visibleColumns !== undefined) row.visible_columns = data.visibleColumns;
  if (data.entityType !== undefined) row.entity_type = data.entityType;
  
  return row;
}

/**
 * Main hook for Smart Lists CRUD operations
 */
export function useSmartLists({
  userId,
  entityType = 'contacts', // 'contacts' or 'deals'
} = {}) {
  // State
  const [smartLists, setSmartLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedListId, setSelectedListId] = useState(null);
  
  // Refs
  const subscriptionRef = useRef(null);
  
  /**
   * Fetch all smart lists (user's personal + global)
   */
  const fetchSmartLists = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setError('Supabase is not configured');
      setLoading(false);
      return;
    }
    
    if (!userId) {
      setError('User ID is required');
      setLoading(false);
      return;
    }
    
    const supabase = getSupabase();
    setLoading(true);
    setError(null);
    
    try {
      // Fetch user's own lists + global lists
      const { data, error: queryError } = await supabase
        .from('crm_smart_lists')
        .select('*')
        .or(`created_by.eq.${userId},is_global.eq.true`)
        .eq('entity_type', entityType)
        .order('usage_count', { ascending: false })
        .order('name', { ascending: true });
      
      if (queryError) throw queryError;
      
      const transformedLists = (data || []).map(transformSmartListToCamelCase);
      setSmartLists(transformedLists);
      
    } catch (err) {
      console.error('Error fetching smart lists:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, entityType]);
  
  /**
   * Create a new smart list
   */
  const createSmartList = useCallback(async (listData) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const supabase = getSupabase();
    
    const insertData = {
      ...transformSmartListToSnakeCase(listData),
      created_by: userId,
      entity_type: entityType,
    };
    
    const { data, error: insertError } = await supabase
      .from('crm_smart_lists')
      .insert(insertData)
      .select()
      .single();
    
    if (insertError) throw insertError;
    
    const newList = transformSmartListToCamelCase(data);
    setSmartLists(prev => [...prev, newList]);
    
    return newList;
  }, [userId, entityType]);
  
  /**
   * Update an existing smart list
   */
  const updateSmartList = useCallback(async (listId, updates) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    
    const supabase = getSupabase();
    
    const updateData = transformSmartListToSnakeCase(updates);
    
    const { data, error: updateError } = await supabase
      .from('crm_smart_lists')
      .update(updateData)
      .eq('id', listId)
      .select()
      .single();
    
    if (updateError) throw updateError;
    
    const updatedList = transformSmartListToCamelCase(data);
    setSmartLists(prev => prev.map(list => 
      list.id === listId ? updatedList : list
    ));
    
    return updatedList;
  }, []);
  
  /**
   * Delete a smart list
   */
  const deleteSmartList = useCallback(async (listId) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    
    const supabase = getSupabase();
    
    const { error: deleteError } = await supabase
      .from('crm_smart_lists')
      .delete()
      .eq('id', listId);
    
    if (deleteError) throw deleteError;
    
    setSmartLists(prev => prev.filter(list => list.id !== listId));
    
    if (selectedListId === listId) {
      setSelectedListId(null);
    }
  }, [selectedListId]);
  
  /**
   * Increment usage count and update last used timestamp
   */
  const trackUsage = useCallback(async (listId) => {
    if (!isSupabaseConfigured()) return;
    
    const supabase = getSupabase();
    
    try {
      // Use RPC or raw SQL to increment
      const { error } = await supabase
        .from('crm_smart_lists')
        .update({
          usage_count: supabase.rpc ? undefined : 1, // Placeholder
          last_used_at: new Date().toISOString(),
        })
        .eq('id', listId);
      
      // Also increment usage_count (done separately since we can't do increment in one call easily)
      await supabase.rpc('increment_smart_list_usage', { list_id: listId }).catch(() => {
        // RPC might not exist, silently fail
      });
      
      if (!error) {
        setSmartLists(prev => prev.map(list => 
          list.id === listId 
            ? { ...list, usageCount: (list.usageCount || 0) + 1, lastUsedAt: new Date().toISOString() }
            : list
        ));
      }
    } catch (err) {
      // Non-critical, don't throw
      console.warn('Failed to track smart list usage:', err);
    }
  }, []);
  
  /**
   * Set a smart list as default for this user
   */
  const setAsDefault = useCallback(async (listId) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    
    const supabase = getSupabase();
    
    // First, unset any existing default for this user/entity type
    const { error: unsetError } = await supabase
      .from('crm_smart_lists')
      .update({ is_default: false })
      .eq('created_by', userId)
      .eq('entity_type', entityType)
      .eq('is_default', true);
    
    if (unsetError) throw unsetError;
    
    // Set the new default
    const { error } = await supabase
      .from('crm_smart_lists')
      .update({ is_default: true })
      .eq('id', listId);
    
    if (error) throw error;
    
    setSmartLists(prev => prev.map(list => ({
      ...list,
      isDefault: list.id === listId,
    })));
  }, [userId, entityType]);
  
  /**
   * Unset the default smart list (remove default without setting a new one)
   */
  const unsetDefault = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured');
    }
    
    const supabase = getSupabase();
    
    const { error } = await supabase
      .from('crm_smart_lists')
      .update({ is_default: false })
      .eq('created_by', userId)
      .eq('entity_type', entityType)
      .eq('is_default', true);
    
    if (error) throw error;
    
    setSmartLists(prev => prev.map(list => ({
      ...list,
      isDefault: false,
    })));
  }, [userId, entityType]);
  
  /**
   * Duplicate a smart list
   */
  const duplicateSmartList = useCallback(async (listId) => {
    const originalList = smartLists.find(l => l.id === listId);
    if (!originalList) {
      throw new Error('Smart list not found');
    }
    
    const newList = await createSmartList({
      name: `${originalList.name} (Copy)`,
      description: originalList.description,
      filters: originalList.filters,
      sortBy: originalList.sortBy,
      sortDirection: originalList.sortDirection,
      visibleColumns: originalList.visibleColumns,
      isGlobal: false, // Copies are always personal
      isDefault: false,
    });
    
    return newList;
  }, [smartLists, createSmartList]);
  
  /**
   * Get the selected smart list
   */
  const selectedList = smartLists.find(l => l.id === selectedListId) || null;
  
  /**
   * Get the default smart list
   */
  const defaultList = smartLists.find(l => l.isDefault) || null;
  
  /**
   * Separate personal and global lists
   */
  const personalLists = smartLists.filter(l => l.createdBy === userId && !l.isGlobal);
  const globalLists = smartLists.filter(l => l.isGlobal);
  
  // Set up realtime subscription
  useEffect(() => {
    if (!isSupabaseConfigured() || !userId) return;
    
    const supabase = getSupabase();
    
    const channel = supabase
      .channel(`smart-lists-changes-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_smart_lists',
        },
        (payload) => {
          fetchSmartLists();
        }
      )
      .subscribe();
    
    subscriptionRef.current = channel;
    
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [userId, fetchSmartLists]);
  
  // Fetch on mount
  useEffect(() => {
    fetchSmartLists();
  }, [fetchSmartLists]);
  
  return {
    // Data
    smartLists,
    personalLists,
    globalLists,
    selectedList,
    defaultList,
    
    // Selection
    selectedListId,
    setSelectedListId,
    
    // Status
    loading,
    error,
    
    // CRUD actions
    createSmartList,
    updateSmartList,
    deleteSmartList,
    duplicateSmartList,
    
    // Other actions
    trackUsage,
    setAsDefault,
    unsetDefault,
    refresh: fetchSmartLists,
  };
}

export default useSmartLists;
